<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import formsAPI from 'dashboard/api/forms';
import { downloadCsvFile } from 'dashboard/helper/downloadHelper';

const props = defineProps({
  formId: { type: [String, Number], required: true },
  fields: { type: Array, default: () => [] },
});

const { t } = useI18n();
const respostas = ref([]);
const total = ref(0);
const pagina = ref(1);
const carregando = ref(true);
const erro = ref('');

// Colunas: os campos do formulário, na ordem do editor.
const colunas = computed(() =>
  props.fields
    .filter(campo => campo.type !== 'consent')
    .map(campo => ({ key: campo.key, label: campo.label || campo.key }))
);

const valorDe = (resposta, chave) => {
  const item = resposta.answers?.[chave];
  if (item === undefined || item === null) return '—';
  const valor = typeof item === 'object' ? item.value : item;
  if (valor === null || valor === undefined || valor === '') return '—';
  if (typeof valor === 'object') return valor.filename || '—';
  return String(valor);
};

const dataDe = resposta =>
  new Date(resposta.created_at).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const temMais = computed(() => respostas.value.length < total.value);

const carregar = async (proxima = 1) => {
  carregando.value = true;
  erro.value = '';
  try {
    const { data } = await formsAPI.submissions(props.formId, proxima);
    respostas.value =
      proxima === 1 ? data.payload : [...respostas.value, ...data.payload];
    total.value = data.meta.total;
    pagina.value = proxima;
  } catch {
    erro.value = t('FORMS_MGMT.EDITOR.SUBMISSIONS.ERROR');
  } finally {
    carregando.value = false;
  }
};

const exportando = ref(false);
const erroExportar = ref('');

// O nome vem do servidor (respostas-<slug>-<data>.csv).
const nomeDoArquivo = headers => {
  const disposicao = headers?.['content-disposition'] || '';
  const nome = disposicao.match(/filename="([^"]+)"/)?.[1];
  return nome || `respostas-formulario-${props.formId}.csv`;
};

const exportar = async () => {
  exportando.value = true;
  erroExportar.value = '';
  try {
    const { data, headers } = await formsAPI.submissionsExport(props.formId);
    downloadCsvFile(nomeDoArquivo(headers), data);
  } catch {
    erroExportar.value = t('FORMS_MGMT.EDITOR.SUBMISSIONS.EXPORT_ERROR');
  } finally {
    exportando.value = false;
  }
};

onMounted(() => carregar(1));
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex items-center justify-between gap-3">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('FORMS_MGMT.EDITOR.SUBMISSIONS.COUNT', { count: total }) }}
      </span>
      <button
        type="button"
        class="rounded-lg border border-n-weak px-3 py-1.5 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-1"
        :disabled="!total || exportando"
        @click="exportar"
      >
        {{ t('FORMS_MGMT.EDITOR.SUBMISSIONS.EXPORT') }}
      </button>
    </div>

    <p v-if="erroExportar" class="text-xs text-n-ruby-11">
      {{ erroExportar }}
    </p>

    <p v-if="erro" class="text-xs text-n-ruby-11">{{ erro }}</p>

    <p v-else-if="!total && !carregando" class="text-sm text-n-slate-11">
      {{ t('FORMS_MGMT.EDITOR.SUBMISSIONS.EMPTY') }}
    </p>

    <div v-else class="overflow-x-auto">
      <table class="w-full text-sm">
        <thead>
          <tr class="border-b border-n-weak text-left">
            <th class="whitespace-nowrap px-2 py-2 text-xs text-n-slate-11">
              {{ t('FORMS_MGMT.EDITOR.SUBMISSIONS.DATE') }}
            </th>
            <th
              v-for="coluna in colunas"
              :key="coluna.key"
              class="whitespace-nowrap px-2 py-2 text-xs text-n-slate-11"
            >
              {{ coluna.label }}
            </th>
            <th class="px-2 py-2 text-xs text-n-slate-11">
              {{ t('FORMS_MGMT.EDITOR.SUBMISSIONS.SCORE') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="resposta in respostas"
            :key="resposta.id"
            class="border-b border-n-weak/60"
          >
            <td class="whitespace-nowrap px-2 py-2 text-n-slate-11">
              {{ dataDe(resposta) }}
            </td>
            <td
              v-for="coluna in colunas"
              :key="coluna.key"
              class="px-2 py-2 text-n-slate-12"
            >
              {{ valorDe(resposta, coluna.key) }}
            </td>
            <td class="px-2 py-2 text-n-slate-12">
              {{ resposta.score ?? '—' }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <button
      v-if="temMais"
      type="button"
      class="self-start text-xs font-medium text-n-brand"
      :disabled="carregando"
      @click="carregar(pagina + 1)"
    >
      {{ t('FORMS_MGMT.EDITOR.SUBMISSIONS.MORE') }}
    </button>
  </div>
</template>
